import { useEffect, useState } from 'react';
import { Users, Building2, Activity, DollarSign } from 'lucide-react';
import { adminApi } from '../services/adminApi';

interface StatsCard {
  label: string;
  value: string | number;
  icon: any;
  trend?: string;
}

export default function Overview() {
  const [stats, setStats] = useState<StatsCard[]>([
    { label: 'Total Users', value: '0', icon: Users },
    { label: 'Active Tenants', value: '0', icon: Building2 },
    { label: 'New Today', value: '0', icon: Activity },
    { label: 'MRR', value: '$0', icon: DollarSign },
  ]);
  const [activityFeed, setActivityFeed] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadOverviewData();
  }, []);

  const loadOverviewData = async () => {
    try {
      const [statsData, activity] = await Promise.all([
        adminApi.getOverviewStats(),
        adminApi.getActivityFeed(20),
      ]);

      setStats([
        { label: 'Total Users', value: statsData.total_users || 0, icon: Users },
        { label: 'Active Tenants', value: statsData.active_tenants || 0, icon: Building2 },
        { label: 'New Today', value: statsData.new_users_today || 0, icon: Activity },
        { label: 'MRR', value: `$${statsData.mrr?.toLocaleString() || 0}`, icon: DollarSign },
      ]);

      setActivityFeed(activity.activities || []);
    } catch (error) {
      console.error('[OVERVIEW] Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-normal tracking-tight text-foreground mb-1">
          Overview
        </h1>
        <p className="text-body-small text-muted-foreground font-normal tracking-tight">
          Platform statistics and activity
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground font-normal tracking-tight mb-2">
                  {stat.label}
                </p>
                <p className="text-2xl font-normal tracking-tight text-foreground">
                  {stat.value}
                </p>
              </div>
              <div className="p-2 bg-muted rounded-md">
                <stat.icon className="w-4 h-4 text-muted-foreground" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Activity Feed */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-lg font-normal tracking-tight text-foreground mb-4">
          Recent Activity
        </h2>
        
        {isLoading ? (
          <p className="text-body-small text-muted-foreground">Loading activity...</p>
        ) : activityFeed.length === 0 ? (
          <p className="text-body-small text-muted-foreground">No recent activity</p>
        ) : (
          <div className="space-y-3">
            {activityFeed.map((activity, index) => (
              <div key={index} className="flex items-start gap-3 py-2 border-b border-border last:border-0">
                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-body-small font-normal tracking-tight text-foreground truncate">
                    {activity.description || activity.action}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {activity.created_at ? new Date(activity.created_at).toLocaleString() : 'Just now'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
